import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./quoteslist.css";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";
import { useNavigate } from "react-router-dom";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");
const API_QUOTES = `${API_BASE}/superuser/quotes/`;

function eur(n) {
  const x = Number(n || 0);
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(x);
}

function pct(m) {
  const v = Number(m || 0);
  return `${(v * 100).toFixed(1)}%`;
}

function normalizeStatus(s) {
  return String(s || "draft").toLowerCase();
}

function badgeClass(status) {
  const s = normalizeStatus(status);
  if (s === "validated") return "boBadge boBadgeOk";
  if (s === "paid") return "boBadge boBadgePaid";
  if (s === "in_progress") return "boBadge boBadgeInfo";
  if (s === "delivered") return "boBadge boBadgeNeutral";
  return "boBadge";
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(d);
}

export default function QuotesList() {
  const access = useStore((s) => s.user.access);
  const navigate = useNavigate();

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
      return res;
    },
    [access]
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const res = await apiFetch(API_QUOTES, { method: "GET" });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${text}`.trim());
        }

        const data = await res.json();
        if (alive) setItems(Array.isArray(data) ? data : data?.results || []);
      } catch (e) {
        if (alive) setErr(e?.message || "Erreur lors du chargement.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [apiFetch]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return (items || []).filter((it) => {
      const st = normalizeStatus(it.status);

      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (!needle) return true;

      const hay = [it.id, it.project_id, it.order_number, it.status, it.subtotal, it.total, it.margin_pct, it.client_company_name, it.client_first_name, it.client_last_name, it.client_email]
        .map((x) => String(x ?? ""))
        .join(" ")
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [items, q, statusFilter]);

  const totals = useMemo(() => {
    const count = filtered.length;
    const sum = filtered.reduce((acc, it) => acc + Number(it.total || 0), 0);
    return { count, sum };
  }, [filtered]);

  return (
    <div className="boQuotes">
      <div className="boQuotesHeader">
        <div>
          <h1 className="boQuotesTitle">Devis</h1>
          <div className="boQuotesSub">
            {loading ? "Chargement..." : `${totals.count} devis - Total: ${eur(totals.sum)}`}
          </div>
        </div>

        <div className="boQuotesRight">
          <div className="boQuotesFilters">
            <div className="boInputWrap">
              <input
                className="boInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher (id, statut, total, projet...)"
              />
            </div>

            <select className="boSelect" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="draft">Draft</option>
              <option value="validated">Validated</option>
              <option value="paid">Paid</option>
              <option value="in_progress">In progress</option>
              <option value="delivered">Delivered</option>
            </select>

            <button className="boBtn" onClick={() => window.location.reload()}>
              Rafraîchir
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="boCard boError">
          <div className="boErrorTitle">Erreur</div>
          <div className="boErrorText">{err}</div>
        </div>
      ) : loading ? (
        <div className="boCard">
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="boCard boEmpty">
        
        </div>
      ) : (
        <div className="boCard">
          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Référence interne</th>
                  <th>Statut</th>
                  <th>Projet</th>
                  <th>Client</th>
                  <th>Créé</th>
                  <th className="right">Subtotal</th>
                  <th className="right">Marge</th>
                  <th className="right">Total</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id}>
                    <td className="mono">#{it.id}</td>
                    <td className="mono">{it.order_number || "-"}</td>
                    <td>
                      <span className={badgeClass(it.status)}>{normalizeStatus(it.status)}</span>
                    </td>
                    <td className="mono">{it.project_id ? `#${it.project_id}` : "-"}</td>
                    <td>
                      {it.client_company_name || it.client_email || "-"}
                    </td>
                    <td>{formatDate(it.created || it.created_at)}</td>
                    <td className="right mono">{eur(it.subtotal)}</td>
                    <td className="right mono">{pct(it.margin_pct)}</td>
                    <td className="right mono strong">{eur(it.total)}</td>
                    <td className="right">
                      <button
                        className="boBtn boBtnGhost"
                        onClick={() => navigate(`/bo/quotes/${it.id}`)}
                        title="Ouvrir"
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="boTableFooter">
          </div>
        </div>
      )}
    </div>
  );
}

